import axios, { AxiosInstance, AxiosResponse, ResponseType } from "axios";
import HttpStatus from "http-status-codes";
import qs from "qs";
import cookie from "react-cookies";
import { toggleLoading } from "../components/atoms/loading/loading";
import { showMessage } from "../components/atoms/notification/notification";
import { CookieConstants } from "../constants/local";
import { API_URL, ROUTE_PATHS } from "../constants/url-config";
import {
  MessageType,
  ResponseMessage,
} from "../models/common/response-message";
import { AuthService } from "../services/auth.service";
import HISTORY from "./history.helper";
import i18n from "./i18n";

const axiosInstance = (
  baseURL: string,
  handleErrorAutomatic: boolean,
  successMessage?: string,
  contentType: string = "application/json",
  responseType: ResponseType = "json",
  isShowLoading: boolean = true,
  isShowMessage: boolean = true,
  isDownload: boolean = false
): AxiosInstance => {
  if (isShowLoading) toggleLoading(true);
  const instance = axios.create({
    baseURL: baseURL,
    headers: {
      "Content-Type": contentType,
      Authorization: `Bearer ${cookie.load(CookieConstants.ACCESS_TOKEN)}`,
    },
    responseType: isDownload ? "blob" : responseType,
  });

  instance.interceptors.response.use(
    (response) => {
      if (isShowLoading) toggleLoading(false);
      return response;
    },
    (error) => {
      let _error: ResponseMessage = {
        type: MessageType.error,
        message: i18n.t("Global.DefaultError"),
        code: "",
      };

      if (error.response) {
        console.log(window.location.href);
        console.log(HttpStatus.FORBIDDEN);

        let status = error.response.status;
        console.log(status);
        // when UnAuthorize
        if (
          (status === HttpStatus.UNAUTHORIZED ||
            (status === HttpStatus.FORBIDDEN &&
              (error.response.data?.status_code_text === "Login has expired" ||
                error.response.data?.status_code_text ===
                  "Security token invalid"))) &&
          window.location.href.indexOf(ROUTE_PATHS.SignIn) === -1
        ) {
          const originalRequest = error.config;
          let refreshToken = cookie.load(CookieConstants.REFRESH_TOKEN);

          if (refreshToken && !originalRequest._retry) {
            originalRequest._retry = true;
            return axios
              .post(
                `${API_URL.authService}/refresh_token`,
                {},
                {
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${refreshToken}`,
                  },
                }
              )
              .then((res) => {
                AuthService.setLoginData(res.data);
                originalRequest.headers = {
                  "Content-Type": contentType,
                  Authorization: `Bearer ${res.data.access_token}`,
                };
                return axios(originalRequest);
              })
              .catch((err) => {
                // run after original request finaly
                setTimeout(() => {
                  handleUnAuthorize();
                });

                return Promise.reject(error);
              })
              .finally(() => {
                if (isShowLoading) toggleLoading(false);
              });
          }

          handleUnAuthorize();
        } else if (error.response.data) {
          if (error.response.data.message) {
            _error.message = error.response.data.message;
          } else if (error.response.data.suggestions) {
            _error.message = error.response.data.suggestions;
          } else if (error.response.data.root_cause) {
            _error.message = error.response.data.root_cause;
          } else if (error.response.data.detail) {
            _error.message = error.response.data.detail;
          } else if (typeof error.response.data === "string") {
            _error.message = error.response.data;
          }
        }
      }

      if (isShowLoading) toggleLoading(false);

      showMessage(_error);

      return Promise.reject(error);
    }
  );
  return instance;
};

export const getAsync = (
  baseURL: string,
  url: string,
  params?: { [key: string]: any },
  isShowLoading: boolean = true,
  isShowMessage: boolean = true,
  handleErrorAutomatic: boolean = true,
  isDowload: boolean = false
): Promise<AxiosResponse> => {
  return axiosInstance(
    baseURL,
    handleErrorAutomatic,
    undefined,
    "application/json",
    "text",
    isShowLoading,
    isShowMessage,
    isDowload
  ).get(url, {
    params: params,
    paramsSerializer: function (params) {
      return qs.stringify(params, { arrayFormat: "repeat" });
    },
  });
};

export const postAsync = (
  baseURL: string,
  url: string,
  json?: object,
  successMessage?: string,
  isShowLoading: boolean = true,
  isShowMessage: boolean = true,
  handleErrorAutomatic: boolean = true,
  responseType: ResponseType = "json"
): Promise<AxiosResponse> => {
  return axiosInstance(
    baseURL,
    handleErrorAutomatic,
    successMessage,
    "application/json",
    responseType,
    isShowLoading,
    isShowMessage
  ).post(url, json);
};

export const putAsync = (
  baseURL: string,
  url: string,
  json?: object,
  successMessage?: string,
  isShowLoading: boolean = true,
  isShowMessage: boolean = true,
  handleErrorAutomatic: boolean = true
): Promise<AxiosResponse> => {
  return axiosInstance(
    baseURL,
    handleErrorAutomatic,
    successMessage,
    "application/json",
    "json",
    isShowLoading,
    isShowMessage
  ).put(url, json);
};

export const patchAsync = (
  baseURL: string,
  url: string,
  json?: object,
  successMessage?: string,
  isShowLoading: boolean = true,
  isShowMessage: boolean = true,
  handleErrorAutomatic: boolean = true
): Promise<AxiosResponse> => {
  return axiosInstance(
    baseURL,
    handleErrorAutomatic,
    successMessage,
    "application/json",
    "json",
    isShowLoading,
    isShowMessage
  ).patch(url, json);
};

export const deleteAsync = (
  baseURL: string,
  url: string,
  successMessage?: string,
  isShowLoading: boolean = true,
  isShowMessage: boolean = true,
  handleErrorAutomatic: boolean = true
): Promise<AxiosResponse> => {
  return axiosInstance(
    baseURL,
    handleErrorAutomatic,
    successMessage,
    "application/json",
    "json",
    isShowLoading,
    isShowMessage
  ).delete(url);
};

export const uploadFileAsync = (
  baseURL: string,
  url: string,
  json?: object,
  successMessage?: string,
  isShowLoading: boolean = true,
  isShowMessage: boolean = true,
  handleErrorAutomatic: boolean = true,
  responseType: ResponseType = "json"
): Promise<AxiosResponse> => {
  return axiosInstance(
    baseURL,
    handleErrorAutomatic,
    successMessage,
    "multipart/form-data",
    responseType,
    isShowLoading,
    isShowMessage
  ).post(url, parseFormdata(json));
};

const parseFormdata = (model: any) => {
  const formdata = new FormData();
  Object.keys(model || {}).forEach((p) => {
    if (model[p]) {
      if (Array.isArray(model[p])) {
        (model[p] as Array<any>).forEach((q) => {
          formdata.append(p + "[]", q);
        });
      } else {
        formdata.append(p, model[p]);
      }
    }
  });

  return formdata;
};

function handleUnAuthorize() {
  AuthService.logout();
  const url = window.location.pathname + window.location.search;
  HISTORY.replace({
    pathname: ROUTE_PATHS.SignIn,
    state: { nextPathname: url },
  });
}
